import { matchesOption, displayOptionInput } from "../../../i18n/options";
import { useLanguage, t } from "../../../i18n";
import React, { useMemo, useRef, useState, useEffect } from "react";
import { Row, Col, Button, Form, Spinner } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import imageCompression from "browser-image-compression";
import { HiOutlinePhotograph, HiOutlineX } from "react-icons/hi";
import "./ListingForm.css";
import getCities from "../../../features/profile/services/getCities";
import getNeighbourhoods from "../../../features/profile/services/getNeighbourhoods";
import getPropertyTypes from "../../../features/profile/services/getPropertyTypes";
import getCommunityPreferences from "../../../features/profile/services/getCommunityPreferences";
const roomTypeOptions = [{
  label: "Bedspace",
  value: "bedspace"
}, {
  label: "Shared Room",
  value: "shared room"
}, {
  label: "Private Room",
  value: "private room"
}, {
  label: "Studio Room",
  value: "studio room"
}, {
  label: "Partition Room",
  value: "partition room"
}];
const genderOptions = [{
  label: "Male Only",
  value: "male"
}, {
  label: "Female Only",
  value: "female"
}];
const kitchenAccessOptions = [{
  label: "Full Access",
  value: "full"
}, {
  label: "Shared Access",
  value: "shared"
}, {
  label: "No Access",
  value: "none"
}];
const acTypeOptions = [{
  label: "Split AC",
  value: "split"
}, {
  label: "Window AC",
  value: "window"
}, {
  label: "Central AC",
  value: "central"
}];
const rentPaymentPlanOptions = [{
  label: "Monthly",
  value: "monthly"
}, {
  label: "3 Months",
  value: "3_months"
}, {
  label: "6 Months",
  value: "6_months"
}, {
  label: "12 Months",
  value: "12_months"
}];
const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;
const MAX_ROOM_IMAGES = 15;
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const ACCEPTED_IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".webp"];
const COMPRESSED_MAX_SIZE_MB = 1;
const COMPRESSED_MAX_WIDTH_OR_HEIGHT = 1600;
const normalizeSubmissionTextValue = value => String(value || "").trim().replace(/_/g, " ").replace(/\s+/g, " ").toLowerCase();
const normalizeOption = option => {
  if (typeof option === "object" && option !== null) {
    return {
      value: String(option.value ?? option.id ?? ""),
      label: String(option.label ?? option.name ?? ""),
      raw: option
    };
  }
  return {
    value: String(option),
    label: String(option),
    raw: option
  };
};
const buildUntouchedState = (fields = []) => fields.reduce((acc, field) => {
  acc[field] = false;
  return acc;
}, {});
const normalizeMultiSelectValue = (selectedValues, allOptions) => {
  const normalizedOptions = allOptions.map(normalizeOption);
  const allValues = normalizedOptions.map(item => item.value);
  return selectedValues.filter(item => allValues.includes(String(item)));
};
const isExistingImageUrl = item => typeof item === "string" && item.trim() !== "";
const isFileLikeObject = item => typeof File !== "undefined" && item instanceof File;
const isExistingImageObject = item => typeof item === "object" && item !== null && !isFileLikeObject(item) && (item.isExistingImage === true || item.id !== undefined || item.image_id !== undefined || item.url || item.image_path);
const getExistingImageUrl = item => {
  if (isExistingImageUrl(item)) return item;
  if (isExistingImageObject(item)) {
    return item.url || item.image_path || "";
  }
  return "";
};
const getExistingImageId = item => {
  if (!isExistingImageObject(item)) return "";
  return String(item.id ?? item.image_id ?? "");
};
const isValidImageType = file => {
  if (!file) return false;
  if (file.type && ACCEPTED_IMAGE_TYPES.includes(file.type.toLowerCase())) {
    return true;
  }
  const fileName = file.name ? file.name.toLowerCase() : "";
  return ACCEPTED_IMAGE_EXTENSIONS.some(ext => fileName.endsWith(ext));
};
const getInvalidImageMessages = (files = []) => {
  const messages = [];
  files.forEach(file => {
    if (!isValidImageType(file)) {
      messages.push(`${file.name} is not a supported image format. Please upload JPG, JPEG, PNG, or WEBP files only.`);
      return;
    }
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      messages.push(`${file.name} exceeds the 5 MB size limit.`);
    }
  });
  return messages;
};
const getCompressedFileName = (fileName = "") => {
  const safeName = String(fileName || "").trim();
  if (!safeName) return "room-image.jpg";
  const lastDotIndex = safeName.lastIndexOf(".");
  const baseName = lastDotIndex > 0 ? safeName.slice(0, lastDotIndex) : safeName;
  const extension = lastDotIndex > 0 ? safeName.slice(lastDotIndex).toLowerCase() : ".jpg";
  return `${baseName}${extension}`;
};
const compressSingleImage = async file => {
  if (!isFileLikeObject(file)) return file;
  if (!isValidImageType(file)) return file;
  const compressionOptions = {
    maxSizeMB: COMPRESSED_MAX_SIZE_MB,
    maxWidthOrHeight: COMPRESSED_MAX_WIDTH_OR_HEIGHT,
    useWebWorker: true,
    initialQuality: 0.8,
    fileType: file.type && ACCEPTED_IMAGE_TYPES.includes(file.type.toLowerCase()) ? file.type : "image/jpeg"
  };
  const compressedBlob = await imageCompression(file, compressionOptions);
  const compressedFile = new File([compressedBlob], file.name, {
    type: compressedBlob.type || file.type || "image/jpeg",
    lastModified: Date.now()
  });
  return compressedFile;
};
const compressImageFiles = async (files = []) => {
  const compressedFiles = [];
  for (const file of files) {
    const compressedFile = await compressSingleImage(file);
    compressedFiles.push(compressedFile);
  }
  return compressedFiles;
};
const stepOneFields = ["roomTitle", "monthlyRent", "locationAreaStreet", "city", "district", "availableMode", "availableFrom"];
const stepTwoFields = ["houseType", "roomType", "genderPreference", "communityPreference"];
const stepThreeFields = ["bedrooms", "bathrooms", "kitchenAccess", "acType", "electricityIncluded", "waterBillIncluded", "parkingAvailable", "currentOccupants", "rentPaymentPlan", "minimumStay", "isFurnished", "allowWhatsAppContact", "allowDirectCalls", "contactPreference"];
const stepFourFields = ["roomImages", "description"];
const allFormFields = [...stepOneFields, ...stepTwoFields, ...stepThreeFields, ...stepFourFields];
const stepFieldMap = {
  1: stepOneFields,
  2: stepTwoFields,
  3: stepThreeFields,
  4: stepFourFields
};
export const listingInitialValues = {
  roomTitle: "",
  locationAreaStreet: "",
  district: "",
  city: "",
  monthlyRent: "",
  availableMode: "date",
  availableFrom: "",
  houseType: "",
  roomType: "",
  genderPreference: "",
  communityPreference: [],
  bedrooms: 1,
  bathrooms: 1,
  kitchenAccess: "",
  acType: "",
  electricityIncluded: false,
  waterBillIncluded: false,
  parkingAvailable: false,
  currentOccupants: 1,
  rentPaymentPlan: "",
  minimumStay: 1,
  isFurnished: false,
  allowWhatsAppContact: false,
  allowDirectCalls: true,
  description: "",
  roomImages: []
};
const mapListingFormToPayload = values => {
  const roomImageFiles = Array.isArray(values.roomImages) ? values.roomImages.filter(item => typeof File !== "undefined" && item instanceof File) : [];
  const payload = {
    listing: {
      title: values.roomTitle?.trim() || "",
      description: values.description?.trim() || "",
      address: values.locationAreaStreet?.trim() || "",
      city_id: Number(values.city),
      neighbourhood_id: Number(values.district),
      rent: Number(values.monthlyRent),
      house_type: values.houseType || "",
      room_type: values.roomType || "",
      gender_preference: values.genderPreference || "",
      bedrooms: Number(values.bedrooms),
      bathrooms: Number(values.bathrooms),
      current_occupants: Number(values.currentOccupants),
      kitchen_access: values.kitchenAccess || "",
      ac_type: values.acType || "",
      electricity_included: Boolean(values.electricityIncluded),
      water_bill_included: Boolean(values.waterBillIncluded),
      parking_available: Boolean(values.parkingAvailable),
      rent_payment_plan: values.rentPaymentPlan || "",
      minimum_stay: values.minimumStay !== "" && values.minimumStay !== null ? Number(values.minimumStay) : null,
      furnished: Boolean(values.isFurnished),
      allow_whatsapp: Boolean(values.allowWhatsAppContact),
      allow_calls: Boolean(values.allowDirectCalls),
      availability_type: values.availableMode || "date",
      available_from: values.availableMode === "date" ? values.availableFrom || null : null
    },
    community_preferences: Array.isArray(values.communityPreference) ? values.communityPreference.map(item => Number(item)) : []
  };
  return {
    payload,
    roomImageFiles
  };
};
function RoomImagePreviewCard({
  file,
  index,
  onRemove
}) {
  useLanguage();
  const [previewUrl, setPreviewUrl] = useState("");
  useEffect(() => {
    if (isExistingImageUrl(file) || isExistingImageObject(file)) {
      setPreviewUrl(getExistingImageUrl(file));
      return undefined;
    }
    if (isFileLikeObject(file)) {
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
      return () => {
        URL.revokeObjectURL(objectUrl);
      };
    }
    setPreviewUrl("");
    return undefined;
  }, [file]);
  const imageName = isFileLikeObject(file) ? file?.name || `Room Image ${index + 1}` : `Room Image ${index + 1}`;
  return <div className="bb-create-listing__preview-card">
      <button type="button" className="bb-create-listing__preview-remove" onClick={() => onRemove(index)} aria-label={t(`Remove ${imageName}`)}>

        <HiOutlineX />
      </button>

      <div className="bb-create-listing__preview-image-wrap">
        {previewUrl ? <img src={previewUrl} alt={imageName} className="bb-create-listing__preview-image" /> : <div className="bb-create-listing__preview-fallback">
            <HiOutlinePhotograph />
          </div>}
      </div>

      <div className="bb-create-listing__preview-meta">
        <div className="bb-create-listing__preview-name" title={imageName}>
          {imageName}
        </div>
        {isFileLikeObject(file) ? <div className="bb-create-listing__preview-size">
            {t((file.size / (1024 * 1024)).toFixed(2))}{t(" MB")}
        </div> : null}
      </div>
    </div>;
}
function CounterField({
  label,
  name,
  value,
  setFieldValue,
  setFieldTouched,
  error,
  showError,
  min = 1,
  step = 1,
  helperText,
  onInteract
}) {
  useLanguage();
  const safeValue = Number(value) >= min ? Number(value) : min;
  const handleDecrease = () => {
    const nextValue = Math.max(min, safeValue - step);
    setFieldValue(name, nextValue);
    setFieldTouched(name, true, true);
    if (onInteract) onInteract(name);
  };
  const handleIncrease = () => {
    const nextValue = safeValue + step;
    setFieldValue(name, nextValue);
    setFieldTouched(name, true, true);
    if (onInteract) onInteract(name);
  };
  const handleInputChange = e => {
    const rawValue = e.target.value;
    if (rawValue === "") {
      setFieldValue(name, "");
      setFieldTouched(name, true, true);
      if (onInteract) onInteract(name);
      return;
    }
    const numericValue = Number(rawValue);
    if (!Number.isNaN(numericValue)) {
      setFieldValue(name, numericValue < min ? min : numericValue);
      setFieldTouched(name, true, true);
      if (onInteract) onInteract(name);
    }
  };
  const handleBlur = () => {
    const normalizedValue = value === "" || Number.isNaN(Number(value)) || Number(value) < min ? min : Number(value);
    setFieldValue(name, normalizedValue);
    setFieldTouched(name, true, true);
    if (onInteract) onInteract(name);
  };
  return <Form.Group>
      <Form.Label>{t(label)}</Form.Label>

      <div className={`bb-counter-field ${showError ? "is-invalid" : ""}`}>
        <button type="button" className="bb-counter-field__btn" onClick={handleDecrease} aria-label={t(`Decrease ${t(label)}`)}>

          −
        </button>

        <Form.Control type="number" min={min} step={step} name={name} value={value} onChange={handleInputChange} onBlur={handleBlur} className="bb-counter-field__input" />


        <button type="button" className="bb-counter-field__btn" onClick={handleIncrease} aria-label={t(`Increase ${t(label)}`)}>

          +
        </button>
      </div>

      {helperText ? <div className="bb-listing-form__helper-text">{t(helperText)}</div> : null}

      {showError ? <div className="invalid-feedback d-block">{t(error)}</div> : null}
    </Form.Group>;
}
function MultiSelectTagField({
  label,
  name,
  options,
  values,
  errors,
  setFieldValue,
  setFieldTouched,
  helperText,
  onSelectionChange,
  disabled = false,
  loading = false,
  searchable = false,
  searchPlaceholder,
  showError,
  onInteract,
  optionLabelMap = {}
}) {
  const {
    language
  } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [hasInteracted, setHasInteracted] = useState(false);
  const wrapperRef = useRef(null);
  const searchInputRef = useRef(null);
  const normalizedOptions = useMemo(() => Array.isArray(options) ? options.map(normalizeOption) : [], [options]);
  const selectedValues = Array.isArray(values[name]) ? values[name].map(item => String(item)) : [];
  const filteredOptions = useMemo(() => {
    if (!searchable) return normalizedOptions;
    const keyword = searchKeyword.trim().toLowerCase();
    if (!keyword) return normalizedOptions;
    return normalizedOptions.filter(item => matchesOption(item, keyword, language));
  }, [normalizedOptions, searchable, searchKeyword, language]);
  const selectedLabelMap = useMemo(() => {
    const map = {
      ...optionLabelMap
    };
    normalizedOptions.forEach(item => {
      map[item.value] = item.label;
    });
    return map;
  }, [normalizedOptions, optionLabelMap]);
  useEffect(() => {
    const handleClickOutside = event => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        if (isOpen) {
          setIsOpen(false);
        }
        if (hasInteracted) {
          setFieldTouched(name, true, true);
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [hasInteracted, isOpen, name, setFieldTouched]);
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, searchable]);
  const registerInteraction = () => {
    setHasInteracted(true);
    if (onInteract) onInteract(name);
  };
  const isSelected = optionValue => selectedValues.includes(String(optionValue));
  const updateValues = updatedValues => {
    const normalizedValues = normalizeMultiSelectValue(updatedValues, normalizedOptions);
    setFieldValue(name, normalizedValues);
    registerInteraction();
    setFieldTouched(name, true, true);
    if (onSelectionChange) {
      onSelectionChange(normalizedValues);
    }
  };
  const handleToggleOption = optionValue => {
    if (disabled) return;
    const stringValue = String(optionValue);
    let updatedValues = [...selectedValues];
    registerInteraction();
    if (updatedValues.includes(stringValue)) {
      updatedValues = updatedValues.filter(item => item !== stringValue);
    } else {
      updatedValues.push(stringValue);
    }
    updateValues(updatedValues);
  };
  const handleRemoveTag = (optionValueToRemove, event) => {
    event.stopPropagation();
    if (disabled) return;
    registerInteraction();
    const updatedValues = selectedValues.filter(item => item !== String(optionValueToRemove));
    updateValues(updatedValues);
  };
  const handleClearAll = event => {
    event.stopPropagation();
    if (disabled) return;
    registerInteraction();
    updateValues([]);
  };
  return <Form.Group>
      <Form.Label>{t(label)}</Form.Label>

      <div ref={wrapperRef} className={`bb-tag-multiselect ${showError ? "is-invalid" : ""} ${disabled ? "disabled" : ""}`}>

        <div className="bb-tag-multiselect__control" onClick={() => {
        if (disabled) return;
        setIsOpen(prev => !prev);
        registerInteraction();
      }} role="button" tabIndex={disabled ? -1 : 0} onKeyDown={e => {
        if (disabled) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setIsOpen(prev => !prev);
          registerInteraction();
        }
      }}>

          <div className="bb-tag-multiselect__value">
            {loading ? <span className="bb-tag-multiselect__placeholder">{t("Loading ")}
              {t(typeof label === "string" ? label : "options")}...
              </span> : selectedValues.length === 0 ? <span className="bb-tag-multiselect__placeholder">{t("Select ")}
              {t(typeof label === "string" ? label : "options")}
              </span> : <div className="bb-tag-multiselect__tags">
                {selectedValues.map(item => <span key={item} className="bb-tag-multiselect__tag">
                    {t(selectedLabelMap[item] || item)}
                    <button type="button" className="bb-tag-multiselect__tag-remove" onClick={e => handleRemoveTag(item, e)}>

                      ×
                    </button>
                  </span>)}
              </div>}
          </div>

          <div className="bb-tag-multiselect__actions">
            {selectedValues.length > 0 && !disabled && <button type="button" className="bb-tag-multiselect__clear" onClick={handleClearAll}>{t("Clear")}


            </button>}
            <span className={`bb-tag-multiselect__arrow ${isOpen ? "open" : ""}`}>
              ▾
            </span>
          </div>
        </div>

        {isOpen && !disabled && <div className="bb-tag-multiselect__menu">
            {searchable ? <div className="bb-tag-multiselect__search-wrap">
                <Form.Control ref={searchInputRef} type="text" value={searchKeyword} placeholder={t(searchPlaceholder || "Search")} onChange={e => {
            setSearchKeyword(e.target.value);
            registerInteraction();
          }} onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()} />

              </div> : null}

            {filteredOptions.length > 0 ? filteredOptions.map(option => <button key={option.value} type="button" className={`bb-tag-multiselect__option ${isSelected(option.value) ? "active" : ""}`} onClick={() => handleToggleOption(option.value)}>

                  <span className="bb-tag-multiselect__checkbox">
                    {t(isSelected(option.value) ? "✓" : "")}
                  </span>
                  <span>{t(option.label)}</span>
                </button>) : <div className="bb-tag-multiselect__empty">{t("No relevant options found")}

          </div>}
          </div>}
      </div>

      {helperText ? <div className="bb-listing-form__helper-text">{t(helperText)}</div> : null}

      {showError ? <div className="invalid-feedback d-block">{t(errors[name])}</div> : null}
    </Form.Group>;
}
function SearchableSingleSelectField({
  label,
  name,
  options,
  value,
  errors,
  setFieldValue,
  setFieldTouched,
  placeholder,
  disabled = false,
  loading = false,
  emptyMessage,
  onSelectionChange,
  onTypingChange,
  showError,
  onInteract
}) {
  const {
    language
  } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [hasFocused, setHasFocused] = useState(false);
  const wrapperRef = useRef(null);
  const normalizedOptions = useMemo(() => Array.isArray(options) ? options.map(normalizeOption) : [], [options]);
  const selectedOption = useMemo(() => {
    return normalizedOptions.find(item => item.value === String(value)) || null;
  }, [normalizedOptions, value]);
  const [inputValue, setInputValue] = useState(selectedOption?.label || "");
  useEffect(() => {
    setInputValue(selectedOption?.label || "");
  }, [selectedOption]);
  useEffect(() => {
    const handleClickOutside = event => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        if (isOpen) {
          setIsOpen(false);
        }
        if (hasInteracted || hasFocused) {
          setFieldTouched(name, true, true);
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [hasFocused, hasInteracted, isOpen, name, setFieldTouched]);
  const filteredOptions = useMemo(() => {
    const keyword = inputValue.trim().toLowerCase();
    if (!keyword) return normalizedOptions;
    return normalizedOptions.filter(item => matchesOption(item, keyword, language));
  }, [inputValue, normalizedOptions, language]);
  const registerInteraction = () => {
    setHasInteracted(true);
    if (onInteract) onInteract(name);
  };
  const handleInputChange = e => {
    if (disabled) return;
    const nextValue = e.target.value;
    setInputValue(nextValue);
    setFieldValue(name, "");
    setIsOpen(true);
    registerInteraction();
    if (onTypingChange) {
      onTypingChange(nextValue);
    }
  };
  const handleSelectOption = option => {
    if (disabled) return;
    setInputValue(option.label);
    setFieldValue(name, option.value);
    setFieldTouched(name, true, true);
    setIsOpen(false);
    registerInteraction();
    if (onSelectionChange) {
      onSelectionChange(option);
    }
  };
  return <Form.Group>
      <Form.Label>{t(label)}</Form.Label>

      <div ref={wrapperRef} className={`bb-search-select ${showError ? "is-invalid" : ""} ${disabled ? "disabled" : ""}`}>

        <Form.Control type="text" name={name} value={displayOptionInput(inputValue, normalizedOptions)} placeholder={t(loading ? `Loading...` : placeholder)} autoComplete="off" onFocus={() => {
        if (disabled) return;
        setIsOpen(true);
        setHasFocused(true);
      }} onChange={handleInputChange} onBlur={() => {
        if (hasInteracted || hasFocused) {
          setFieldTouched(name, true, true);
        }
      }} isInvalid={showError} disabled={disabled} />


        {isOpen && !disabled && <div className="bb-search-select__menu">
            {filteredOptions.length > 0 ? filteredOptions.map(option => <button key={option.value} type="button" className={`bb-search-select__option ${String(value) === option.value ? "active" : ""}`} onMouseDown={e => e.preventDefault()} onClick={() => handleSelectOption(option)}>

                  {t(option.label)}
                </button>) : <div className="bb-search-select__empty">
                {t(emptyMessage || "No relevant option found")}
              </div>}
          </div>}
      </div>

      {showError ? <div className="invalid-feedback d-block">{t(errors[name])}</div> : null}
    </Form.Group>;
}
export const listingValidationSchema = Yup.object({
  roomTitle: Yup.string().trim().min(5, "Room Title should be at least 5 characters").required("Room Title is required"),
  locationAreaStreet: Yup.string().trim().required("Location / Area / Street is required"),
  district: Yup.string().required("District / Neighbourhood is required"),
  city: Yup.string().required("City is required"),
  monthlyRent: Yup.number().typeError("Monthly Rent must be a number").min(100, "Monthly Rent must be at least 100 SAR").max(50000, "Monthly Rent cannot exceed 50000 SAR").required("Monthly Rent is required"),
  houseType: Yup.string().required("House Type is required"),
  roomType: Yup.string().required("Room Type is required"),
  genderPreference: Yup.string().required("Gender Preference is required"),
  communityPreference: Yup.array().of(Yup.string()).min(1, "At least one Community Preference is required").required("Community Preference is required"),
  availableMode: Yup.string().required("Availability selection is required"),
  availableFrom: Yup.string().when("availableMode", {
    is: "date",
    then: schema => schema.required("Available From date is required").test("not-in-past", "Available From date cannot be in the past", function (value) {
      if (!value) return false;
      const today = new Date().toISOString().split("T")[0];
      return value >= today;
    }),
    otherwise: schema => schema.notRequired()
  }),
  bedrooms: Yup.number().typeError("Total Bedrooms must be a number").min(1, "Total Bedrooms must be at least 1").required("Total Bedrooms is required"),
  bathrooms: Yup.number().typeError("Total Bathrooms must be a number").min(1, "Total Bathrooms must be at least 1").required("Total Bathrooms is required"),
  kitchenAccess: Yup.string().required("Kitchen Access is required"),
  acType: Yup.string().required("AC Type is required"),
  electricityIncluded: Yup.boolean(),
  waterBillIncluded: Yup.boolean(),
  parkingAvailable: Yup.boolean(),
  currentOccupants: Yup.number().typeError("Current Occupants must be a number").min(1, "Current Occupants must be at least 1").required("Current Occupants is required"),
  rentPaymentPlan: Yup.string().required("Rent Payment Plan is required"),
  minimumStay: Yup.number().typeError("Minimum Stay must be a number").min(1, "Minimum Stay must be at least 1").notRequired(),
  description: Yup.string().trim().min(20, "Description should be at least 20 characters").required("Description is required"),
  roomImages: Yup.array().of(Yup.mixed()).min(1, "At least 1 room image is required").max(15, "You can upload a maximum of 15 room images").test("valid-image-types", "Only JPG, JPEG, PNG, and WEBP image files are allowed.", files => {
    if (!Array.isArray(files) || files.length === 0) return false;
    return files.every(file => {
      if (isExistingImageUrl(file) || isExistingImageObject(file)) return true;
      if (!isFileLikeObject(file)) return false;
      return isValidImageType(file);
    });
  }).test("valid-image-size", "Each image must be 5 MB or smaller.", files => {
    if (!Array.isArray(files) || files.length === 0) return false;
    return files.every(file => {
      if (isExistingImageUrl(file) || isExistingImageObject(file)) return true;
      if (!isFileLikeObject(file)) return false;
      return file.size <= MAX_IMAGE_SIZE_BYTES;
    });
  }).required("Room image is required")
});
export default function ListingForm({
  initialValues,
  submitLabel,
  onSubmit,
  onCancel
}) {
  useLanguage();
  const today = new Date().toISOString().split("T")[0];
  const fileInputRef = useRef(null);
  const [isCompressingImages, setIsCompressingImages] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [cityOptions, setCityOptions] = useState([]);
  const [isCitiesLoading, setIsCitiesLoading] = useState(true);
  const [citiesError, setCitiesError] = useState("");
  const [communityPreferenceOptions, setCommunityPreferenceOptions] = useState([]);
  const [isCommunityPreferencesLoading, setIsCommunityPreferencesLoading] = useState(true);
  const [communityPreferencesError, setCommunityPreferencesError] = useState("");
  const [propertyTypeOptions, setPropertyTypeOptions] = useState([]);
  const [isPropertyTypesLoading, setIsPropertyTypesLoading] = useState(true);
  const [propertyTypesError, setPropertyTypesError] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [districtOptions, setDistrictOptions] = useState([]);
  const [isDistrictLoading, setIsDistrictLoading] = useState(false);
  const [roomImagesUploadError, setRoomImagesUploadError] = useState("");
  const [stepAttempted, setStepAttempted] = useState({
    1: false,
    2: false,
    3: false,
    4: false
  });
  const [stepInteractedFields, setStepInteractedFields] = useState({
    1: {},
    2: {},
    3: {},
    4: {}
  });
  const markStepFieldInteracted = (stepNumber, fieldName) => {
    setStepInteractedFields(prev => ({
      ...prev,
      [stepNumber]: {
        ...prev[stepNumber],
        [fieldName]: true
      }
    }));
  };
  const normalizedInitialValues = useMemo(() => ({
    ...listingInitialValues,
    ...initialValues,
    city: initialValues?.city !== undefined && initialValues?.city !== null ? String(initialValues.city) : "",
    district: initialValues?.district !== undefined && initialValues?.district !== null ? String(initialValues.district) : "",
    houseType: initialValues?.houseType !== undefined && initialValues?.houseType !== null ? normalizeSubmissionTextValue(initialValues.houseType) : "",
    roomType: initialValues?.roomType !== undefined && initialValues?.roomType !== null ? normalizeSubmissionTextValue(initialValues.roomType) : "",
    genderPreference: initialValues?.genderPreference !== undefined && initialValues?.genderPreference !== null ? normalizeSubmissionTextValue(initialValues.genderPreference).includes("female") ? "female" : normalizeSubmissionTextValue(initialValues.genderPreference).includes("male") ? "male" : normalizeSubmissionTextValue(initialValues.genderPreference) : "",
    communityPreference: Array.isArray(initialValues?.communityPreference) ? initialValues.communityPreference.map(item => String(item)) : initialValues?.communityPreference ? [String(initialValues.communityPreference)] : [],
    bedrooms: initialValues?.bedrooms !== undefined && initialValues?.bedrooms !== null ? Number(initialValues.bedrooms) || 1 : 1,
    bathrooms: initialValues?.bathrooms !== undefined && initialValues?.bathrooms !== null ? Number(initialValues.bathrooms) || 1 : 1,
    kitchenAccess: initialValues?.kitchenAccess !== undefined && initialValues?.kitchenAccess !== null ? String(initialValues.kitchenAccess) : "",
    acType: initialValues?.acType !== undefined && initialValues?.acType !== null ? String(initialValues.acType) : "",
    electricityIncluded: typeof initialValues?.electricityIncluded === "boolean" ? initialValues.electricityIncluded : false,
    waterBillIncluded: typeof initialValues?.waterBillIncluded === "boolean" ? initialValues.waterBillIncluded : false,
    parkingAvailable: typeof initialValues?.parkingAvailable === "boolean" ? initialValues.parkingAvailable : false,
    currentOccupants: initialValues?.currentOccupants !== undefined && initialValues?.currentOccupants !== null ? Number(initialValues.currentOccupants) || 1 : 1,
    rentPaymentPlan: initialValues?.rentPaymentPlan !== undefined && initialValues?.rentPaymentPlan !== null ? String(initialValues.rentPaymentPlan) : "",
    minimumStay: initialValues?.minimumStay !== undefined && initialValues?.minimumStay !== null ? Number(initialValues.minimumStay) || 1 : 1,
    roomImages: Array.isArray(initialValues?.roomImages) ? initialValues.roomImages : [],
    allowWhatsAppContact: typeof initialValues?.allowWhatsAppContact === "boolean" ? initialValues.allowWhatsAppContact : false,
    allowDirectCalls: typeof initialValues?.allowDirectCalls === "boolean" ? initialValues.allowDirectCalls : true
  }), [initialValues]);
  useEffect(() => {
    setSelectedCity(normalizedInitialValues.city || "");
  }, [normalizedInitialValues.city]);
  useEffect(() => {
    let isMounted = true;
    const loadCities = async () => {
      try {
        setIsCitiesLoading(true);
        setCitiesError("");
        const response = await getCities();
        const isSuccess = response?.is_active === true || response?.status === true;
        const citiesArray = Array.isArray(response?.data) ? response.data : [];
        const mappedCities = citiesArray.filter(city => city?.id && city?.name).map(city => ({
          value: String(city.id),
          label: city.name
        }));
        if (isMounted) {
          if (isSuccess) {
            setCityOptions(mappedCities);
            setCitiesError("");
          } else {
            setCityOptions([]);
            setCitiesError("Unable to load cities.");
          }
        }
      } catch (error) {
        if (isMounted) {
          setCityOptions([]);
          setCitiesError(error?.response?.data?.message || error?.message || "Failed to load cities.");
        }
      } finally {
        if (isMounted) {
          setIsCitiesLoading(false);
        }
      }
    };
    loadCities();
    return () => {
      isMounted = false;
    };
  }, []);
  useEffect(() => {
    let isMounted = true;
    const loadCommunityPreferences = async () => {
      try {
        setIsCommunityPreferencesLoading(true);
        setCommunityPreferencesError("");
        const response = await getCommunityPreferences();
        const isSuccess = response?.is_active === true || response?.status === true;
        const communityArray = Array.isArray(response?.data) ? response.data : [];
        const mappedCommunityPreferences = communityArray.filter(item => item?.id && item?.name).map(item => ({
          value: String(item.id),
          label: item.name,
          name_ar: item.name_ar || ""
        }));
        if (isMounted) {
          if (isSuccess) {
            setCommunityPreferenceOptions(mappedCommunityPreferences);
            setCommunityPreferencesError(mappedCommunityPreferences.length === 0 ? "Community preference options will appear after backend records are available." : "");
          } else {
            setCommunityPreferenceOptions([]);
            setCommunityPreferencesError("Unable to load community preference options.");
          }
        }
      } catch (error) {
        if (isMounted) {
          setCommunityPreferenceOptions([]);
          setCommunityPreferencesError(error?.response?.data?.message || error?.message || "Failed to load community preference options.");
        }
      } finally {
        if (isMounted) {
          setIsCommunityPreferencesLoading(false);
        }
      }
    };
    loadCommunityPreferences();
    return () => {
      isMounted = false;
    };
  }, []);
  useEffect(() => {
    let isMounted = true;
    const loadPropertyTypes = async () => {
      try {
        setIsPropertyTypesLoading(true);
        setPropertyTypesError("");
        const response = await getPropertyTypes();
        const isSuccess = response?.is_active === true || response?.status === true;
        const propertyTypesArray = Array.isArray(response?.data) ? response.data : [];
        const mappedPropertyTypes = propertyTypesArray.filter(item => item?.name).map(item => ({
          value: normalizeSubmissionTextValue(item.name),
          label: item.name,
          name_ar: item.name_ar || ""
        }));
        if (isMounted) {
          if (isSuccess) {
            setPropertyTypeOptions(mappedPropertyTypes);
            setPropertyTypesError("");
          } else {
            setPropertyTypeOptions([]);
            setPropertyTypesError("Unable to load property types.");
          }
        }
      } catch (error) {
        if (isMounted) {
          setPropertyTypeOptions([]);
          setPropertyTypesError(error?.response?.data?.message || error?.message || "Failed to load property types.");
        }
      } finally {
        if (isMounted) {
          setIsPropertyTypesLoading(false);
        }
      }
    };
    loadPropertyTypes();
    return () => {
      isMounted = false;
    };
  }, []);
  useEffect(() => {
    let isMounted = true;
    const loadDistricts = async () => {
      if (!selectedCity) {
        setDistrictOptions([]);
        return;
      }
      try {
        setIsDistrictLoading(true);
        const response = await getNeighbourhoods(selectedCity);
        const isSuccess = response?.is_active === true || response?.status === true;
        const neighbourhoodsArray = Array.isArray(response?.data) ? response.data : [];
        if (isMounted) {
          if (isSuccess) {
            const mapped = neighbourhoodsArray.filter(item => item?.id && item?.name && String(item?.city_id) === String(selectedCity)).map(item => ({
              value: String(item.id),
              label: item.name,
              id: item.id,
              name: item.name,
              name_ar: item.name_ar || "",
              city_id: item.city_id
            }));
            setDistrictOptions(mapped);
          } else {
            setDistrictOptions([]);
          }
        }
      } catch (error) {
        if (isMounted) {
          setDistrictOptions([]);
        }
      } finally {
        if (isMounted) {
          setIsDistrictLoading(false);
        }
      }
    };
    loadDistricts();
    return () => {
      isMounted = false;
    };
  }, [selectedCity]);
  const cityOptionValues = useMemo(() => cityOptions.map(item => item.value), [cityOptions]);
  const districtOptionValues = useMemo(() => districtOptions.map(item => item.value), [districtOptions]);
  const propertyTypeOptionValues = useMemo(() => propertyTypeOptions.map(item => item.value), [propertyTypeOptions]);
  const communityPreferenceOptionValues = useMemo(() => communityPreferenceOptions.map(item => item.value), [communityPreferenceOptions]);
  const roomTypeOptionValues = useMemo(() => roomTypeOptions.map(item => item.value), []);
  const genderOptionValues = useMemo(() => genderOptions.map(item => item.value), []);
  const kitchenAccessOptionValues = useMemo(() => kitchenAccessOptions.map(item => item.value), []);
  const acTypeOptionValues = useMemo(() => acTypeOptions.map(item => item.value), []);
  const rentPaymentPlanOptionValues = useMemo(() => rentPaymentPlanOptions.map(item => item.value), []);
  const validationShape = useMemo(() => ({
    roomTitle: Yup.string().trim().min(5, "Room Title should be at least 5 characters").required("Room Title is required"),
    locationAreaStreet: Yup.string().trim().required("Location / Area / Street is required"),
    district: Yup.string().required("District / Neighbourhood is required").oneOf(districtOptionValues, "Please select a valid District / Neighbourhood"),
    city: Yup.string().required("City is required").oneOf(cityOptionValues, "Please select a valid City"),
    monthlyRent: Yup.number().typeError("Monthly Rent must be a number").min(100, "Monthly Rent must be at least 100 SAR").max(50000, "Monthly Rent cannot exceed 50000 SAR").required("Monthly Rent is required"),
    houseType: Yup.string().required("House Type is required").oneOf(propertyTypeOptionValues, "Please select a valid House Type"),
    roomType: Yup.string().required("Room Type is required").oneOf(roomTypeOptionValues, "Please select a valid Room Type"),
    genderPreference: Yup.string().required("Gender Preference is required").oneOf(genderOptionValues, "Please select a valid Gender Preference"),
    communityPreference: Yup.array().of(Yup.string().oneOf(communityPreferenceOptionValues)).min(1, "At least one Community Preference is required").required("Community Preference is required"),
    availableMode: Yup.string().required("Availability selection is required"),
    availableFrom: Yup.string().when("availableMode", {
      is: "date",
      then: schema => schema.required("Available From date is required").test("not-in-past", "Available From date cannot be in the past", function (value) {
        if (!value) return false;
        const currentDate = new Date().toISOString().split("T")[0];
        return value >= currentDate;
      }),
      otherwise: schema => schema.notRequired()
    }),
    bedrooms: Yup.number().typeError("Total Bedrooms must be a number").min(1, "Total Bedrooms must be at least 1").required("Total Bedrooms is required"),
    bathrooms: Yup.number().typeError("Total Bathrooms must be a number").min(1, "Total Bathrooms must be at least 1").required("Total Bathrooms is required"),
    kitchenAccess: Yup.string().required("Kitchen Access is required").oneOf(kitchenAccessOptionValues, "Please select a valid Kitchen Access"),
    acType: Yup.string().required("AC Type is required").oneOf(acTypeOptionValues, "Please select a valid AC Type"),
    electricityIncluded: Yup.boolean(),
    waterBillIncluded: Yup.boolean(),
    parkingAvailable: Yup.boolean(),
    currentOccupants: Yup.number().typeError("Current Occupants must be a number").min(1, "Current Occupants must be at least 1").required("Current Occupants is required"),
    rentPaymentPlan: Yup.string().required("Rent Payment Plan is required").oneOf(rentPaymentPlanOptionValues, "Please select a valid Rent Payment Plan"),
    minimumStay: Yup.number().typeError("Minimum Stay must be a number").min(1, "Minimum Stay must be at least 1").notRequired(),
    description: Yup.string().trim().min(20, "Description should be at least 20 characters").required("Description is required"),
    roomImages: Yup.array().of(Yup.mixed()).min(1, "At least 1 room image is required").max(15, "You can upload a maximum of 15 room images").test("valid-image-types", "Only JPG, JPEG, PNG, and WEBP image files are allowed.", files => {
      if (!Array.isArray(files) || files.length === 0) return false;
      return files.every(file => {
        if (isExistingImageUrl(file) || isExistingImageObject(file)) {
          return true;
        }
        if (!isFileLikeObject(file)) return false;
        return isValidImageType(file);
      });
    }).test("valid-image-size", "Each image must be 5 MB or smaller.", files => {
      if (!Array.isArray(files) || files.length === 0) return false;
      return files.every(file => {
        if (isExistingImageUrl(file) || isExistingImageObject(file)) {
          return true;
        }
        if (!isFileLikeObject(file)) return false;
        return file.size <= MAX_IMAGE_SIZE_BYTES;
      });
    }).required("Room image is required"),
    isFurnished: Yup.boolean(),
    allowWhatsAppContact: Yup.boolean(),
    allowDirectCalls: Yup.boolean(),
    contactPreference: Yup.mixed().test("at-least-one-contact-method", "Please enable at least one contact method.", function () {
      const {
        allowWhatsAppContact,
        allowDirectCalls
      } = this.parent || {};
      return Boolean(allowWhatsAppContact || allowDirectCalls);
    })
  }), [cityOptionValues, districtOptionValues, propertyTypeOptionValues, communityPreferenceOptionValues, roomTypeOptionValues, genderOptionValues, kitchenAccessOptionValues, acTypeOptionValues, rentPaymentPlanOptionValues]);
  const fullValidationSchema = useMemo(() => Yup.object(validationShape), [validationShape]);
  return <Formik initialValues={normalizedInitialValues} validationSchema={fullValidationSchema} enableReinitialize validateOnMount={false} onSubmit={(values, formikHelpers) => {
    const {
      payload,
      roomImageFiles
    } = mapListingFormToPayload(values);
    onSubmit(payload, roomImageFiles, formikHelpers, values);
  }}>

      {({
      values,
      errors,
      touched,
      handleChange,
      handleBlur,
      handleSubmit,
      setFieldValue,
      setFieldTouched,
      setFieldError,
      validateForm,
      setTouched,
      isSubmitting
    }) => {
      const getFieldErrorState = fieldName => {
        const hasError = !!errors[fieldName];
        if (!hasError) return false;
        const stepNumber = Number(Object.keys(stepFieldMap).find(key => stepFieldMap[key].includes(fieldName)));
        if (stepNumber === 1) {
          return !!touched[fieldName] || stepAttempted[1];
        }
        return !!stepInteractedFields[stepNumber]?.[fieldName] || !!stepAttempted[stepNumber];
      };
      const handleStepNext = async () => {
        const currentStepFields = stepFieldMap[currentStep] || [];
        setStepAttempted(prev => ({
          ...prev,
          [currentStep]: true
        }));
        const validationErrors = await validateForm();
        const hasStepErrors = currentStepFields.some(field => !!validationErrors[field]);
        const touchedState = buildUntouchedState(allFormFields);
        currentStepFields.forEach(field => {
          touchedState[field] = true;
        });
        setTouched(touchedState, false);
        if (!hasStepErrors) {
          setCurrentStep(prev => prev + 1);
        }
      };
      const handleStepBack = () => {
        if (currentStep === 1) return;
        setCurrentStep(prev => prev - 1);
        const resetTouchedState = buildUntouchedState(allFormFields);
        stepOneFields.forEach(field => {
          resetTouchedState[field] = touched[field] || false;
        });
        setTouched(resetTouchedState, false);
      };
      const handleFinalSubmit = async () => {
        setStepAttempted(prev => ({
          ...prev,
          4: true,
          3: true
        }));
        const validationErrors = await validateForm();
        const hasErrors = allFormFields.some(field => !!validationErrors[field]);
        if (hasErrors) return;
        handleSubmit();
      };
      const handleRoomImagesChange = async event => {
        const selectedFiles = Array.from(event.currentTarget.files || []);
        markStepFieldInteracted(4, "roomImages");
        setFieldTouched("roomImages", true, true);
        if (!selectedFiles.length) {
          return;
        }
        const typeValidationMessages = selectedFiles.filter(file => !isValidImageType(file)).map(file => `${file.name} is not a supported image format. Please upload JPG, JPEG, PNG, or WEBP files only.`);
        if (typeValidationMessages.length > 0) {
          const combinedMessage = typeValidationMessages.join(" ");
          setRoomImagesUploadError(combinedMessage);
          setFieldError("roomImages", combinedMessage);
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
          return;
        }
        const existingImages = Array.isArray(values.roomImages) ? values.roomImages : [];
        if (existingImages.length + selectedFiles.length > MAX_ROOM_IMAGES) {
          const message = `You can upload a maximum of ${MAX_ROOM_IMAGES} room images.`;
          setRoomImagesUploadError(message);
          setFieldError("roomImages", message);
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
          return;
        }
        try {
          setIsCompressingImages(true);
          setRoomImagesUploadError("");
          setFieldError("roomImages", "");
          const compressedFiles = await compressImageFiles(selectedFiles);
          const invalidMessages = getInvalidImageMessages(compressedFiles);
          if (invalidMessages.length > 0) {
            const combinedMessage = invalidMessages.join(" ");
            setRoomImagesUploadError(combinedMessage);
            setFieldError("roomImages", combinedMessage);
            if (fileInputRef.current) {
              fileInputRef.current.value = "";
            }
            return;
          }
          const updatedImages = [...existingImages, ...compressedFiles];
          setFieldValue("roomImages", updatedImages);
          setFieldError("roomImages", "");
        } catch (error) {
          const message = error?.message || "Failed to compress selected images. Please try again.";
          setRoomImagesUploadError(message);
          setFieldError("roomImages", message);
        } finally {
          setIsCompressingImages(false);
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        }
      };
      const handleRemoveImage = indexToRemove => {
        markStepFieldInteracted(4, "roomImages");
        const existingImages = Array.isArray(values.roomImages) ? values.roomImages : [];
        const updatedImages = existingImages.filter((_, index) => index !== indexToRemove);
        setFieldValue("roomImages", updatedImages);
        setFieldTouched("roomImages", true, true);
        setRoomImagesUploadError("");
        if (updatedImages.length === 0) {
          setFieldError("roomImages", "At least 1 room image is required");
        } else {
          setFieldError("roomImages", "");
        }
      };
      const handleToggleContactMethod = (fieldName, checked) => {
        markStepFieldInteracted(3, fieldName);
        markStepFieldInteracted(3, "contactPreference");
        setFieldTouched("contactPreference", true, true);
        const nextWhatsApp = fieldName === "allowWhatsAppContact" ? checked : values.allowWhatsAppContact;
        const nextCalls = fieldName === "allowDirectCalls" ? checked : values.allowDirectCalls;
        setFieldValue(fieldName, checked);
        if (!nextWhatsApp && !nextCalls) {
          setFieldError("contactPreference", "Please enable at least one contact method.");
        } else {
          setFieldError("contactPreference", "");
        }
      };
      return <Form onSubmit={e => e.preventDefault()}>
            <div className="bb-listing-stepper">
              <div className="bb-listing-stepper__items">
                <div className={`bb-listing-stepper__item ${currentStep >= 1 ? "active" : ""}`}>

                  <div className="bb-listing-stepper__circle">1</div>
                  <div className="bb-listing-stepper__label">{t("Basic Information")}</div>
                </div>

                <div className={`bb-listing-stepper__line ${currentStep >= 2 ? "active" : ""}`} />


                <div className={`bb-listing-stepper__item ${currentStep >= 2 ? "active" : ""}`}>

                  <div className="bb-listing-stepper__circle">2</div>
                  <div className="bb-listing-stepper__label">{t("Accommodation Details")}

                  </div>
                </div>

                <div className={`bb-listing-stepper__line ${currentStep >= 3 ? "active" : ""}`} />


                <div className={`bb-listing-stepper__item ${currentStep >= 3 ? "active" : ""}`}>

                  <div className="bb-listing-stepper__circle">3</div>
                  <div className="bb-listing-stepper__label">{t("Room Features & Contact")}

                  </div>
                </div>

                <div className={`bb-listing-stepper__line ${currentStep >= 4 ? "active" : ""}`} />


                <div className={`bb-listing-stepper__item ${currentStep >= 4 ? "active" : ""}`}>

                  <div className="bb-listing-stepper__circle">4</div>
                  <div className="bb-listing-stepper__label">{t("Room Images & Description")}

                  </div>
                </div>
              </div>
            </div>

            {citiesError ? <div className="mb-3 text-danger">{t(citiesError)}</div> : null}
            {communityPreferencesError ? <div className="mb-3 text-danger">{t(communityPreferencesError)}</div> : null}
            {propertyTypesError ? <div className="mb-3 text-danger">{t(propertyTypesError)}</div> : null}

            {currentStep === 1 && <div className="bb-form-section">
                <h3 className="bb-form-section__title">{t("Basic Information")}</h3>

                <Row className="g-4">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>{t("Room Title ")}
                      <span className="bb-required-mark">*</span>
                      </Form.Label>
                      <Form.Control type="text" name="roomTitle" value={values.roomTitle} onChange={handleChange} onBlur={handleBlur} isInvalid={getFieldErrorState("roomTitle")} placeholder={t("e.g. Furnished Private Room in Olaya")} />

                      <Form.Control.Feedback type="invalid">
                        {t(errors.roomTitle)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>{t("Monthly Rent (SAR)")}
                      {t(" ")}
                        <span className="bb-required-mark">*</span>
                      </Form.Label>
                      <Form.Control type="number" min="100" max="50000" name="monthlyRent" value={values.monthlyRent} onChange={handleChange} onBlur={handleBlur} isInvalid={getFieldErrorState("monthlyRent")} placeholder={t("e.g. 1800")} />

                      <Form.Control.Feedback type="invalid">
                        {t(errors.monthlyRent)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>{t("Location / Area / Street")}
                      {t(" ")}
                        <span className="bb-required-mark">*</span>
                      </Form.Label>
                      <Form.Control type="text" name="locationAreaStreet" value={values.locationAreaStreet} onChange={handleChange} onBlur={handleBlur} isInvalid={getFieldErrorState("locationAreaStreet")} placeholder={t("e.g. Building 14, Street 5, Near XYZ")} />

                      <Form.Control.Feedback type="invalid">
                        {t(errors.locationAreaStreet)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <SearchableSingleSelectField label={<>{t("City ")}<span className="bb-required-mark">*</span>
                        </>} name="city" options={cityOptions} value={values.city} errors={errors} setFieldValue={setFieldValue} setFieldTouched={setFieldTouched} placeholder={t("Search city")} emptyMessage={t("No relevant city found")} disabled={isCitiesLoading} loading={isCitiesLoading} showError={getFieldErrorState("city")} onInteract={fieldName => markStepFieldInteracted(1, fieldName)} onTypingChange={() => {
                setSelectedCity("");
                setFieldValue("district", "");
                setDistrictOptions([]);
              }} onSelectionChange={option => {
                const selectedCityValue = String(option.value);
                setFieldValue("city", selectedCityValue);
                setFieldValue("district", "");
                setSelectedCity(selectedCityValue);
                setDistrictOptions([]);
              }} />

                  </Col>

                  <Col md={6}>
                    <SearchableSingleSelectField label={<>{t("District / Neighbourhood")}{" "}
                          <span className="bb-required-mark">*</span>
                        </>} name="district" options={districtOptions} value={values.district} errors={errors} setFieldValue={setFieldValue} setFieldTouched={setFieldTouched} placeholder={t(!values.city ? "Select City First" : "Search district / neighbourhood")} emptyMessage={t("No relevant district / neighbourhood found")} disabled={!values.city || isDistrictLoading} loading={isDistrictLoading} showError={getFieldErrorState("district")} onInteract={fieldName => markStepFieldInteracted(1, fieldName)} />

                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>{t("Availability ")}
                      <span className="bb-required-mark">*</span>
                      </Form.Label>
                      <Form.Select name="availableMode" value={values.availableMode} onChange={e => {
                  handleChange(e);
                  if (e.target.value === "immediate") {
                    setFieldValue("availableFrom", "");
                  }
                }} onBlur={handleBlur} isInvalid={getFieldErrorState("availableMode")}>

                        <option value="date">{t("Available From Date")}</option>
                        <option value="immediate">{t("Immediate")}</option>
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {t(errors.availableMode)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  {values.availableMode === "date" ? <Col md={8}>
                      <Form.Group>
                        <Form.Label>{t("Available From")}
                      {t(" ")}
                          <span className="bb-required-mark">*</span>
                        </Form.Label>
                        <Form.Control type="date" name="availableFrom" min={today} value={values.availableFrom} onChange={handleChange} onBlur={handleBlur} isInvalid={getFieldErrorState("availableFrom")} />

                        <Form.Control.Feedback type="invalid">
                          {t(errors.availableFrom)}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col> : null}
                </Row>
              </div>}

            {currentStep === 2 && <div className="bb-form-section">
                <h3 className="bb-form-section__title">{t("Accommodation Details")}</h3>

                <Row className="g-4">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>{t("House Type ")}
                      <span className="bb-required-mark">*</span>
                      </Form.Label>
                      <Form.Select name="houseType" value={values.houseType} onChange={e => {
                  markStepFieldInteracted(2, "houseType");
                  handleChange(e);
                }} onBlur={e => {
                  markStepFieldInteracted(2, "houseType");
                  handleBlur(e);
                }} isInvalid={getFieldErrorState("houseType")} disabled={isPropertyTypesLoading}>

                        <option value="">
                          {t(isPropertyTypesLoading ? "Loading..." : "Select House Type")}
                        </option>
                        {propertyTypeOptions.map(item => <option key={item.value} value={item.value}>
                            {t(item.label)}
                          </option>)}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {t(errors.houseType)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>{t("Room Type ")}
                      <span className="bb-required-mark">*</span>
                      </Form.Label>
                      <Form.Select name="roomType" value={values.roomType} onChange={e => {
                  markStepFieldInteracted(2, "roomType");
                  handleChange(e);
                }} onBlur={e => {
                  markStepFieldInteracted(2, "roomType");
                  handleBlur(e);
                }} isInvalid={getFieldErrorState("roomType")}>

                        <option value="">{t("Select Room Type")}</option>
                        {roomTypeOptions.map(item => <option key={item.value} value={item.value}>
                            {t(item.label)}
                          </option>)}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {t(errors.roomType)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>{t("Gender Preference")}
                      {t(" ")}
                        <span className="bb-required-mark">*</span>
                      </Form.Label>
                      <Form.Select name="genderPreference" value={values.genderPreference} onChange={e => {
                  markStepFieldInteracted(2, "genderPreference");
                  handleChange(e);
                }} onBlur={e => {
                  markStepFieldInteracted(2, "genderPreference");
                  handleBlur(e);
                }} isInvalid={getFieldErrorState("genderPreference")}>

                        <option value="">{t("Select Gender Preference")}</option>
                        {genderOptions.map(item => <option key={item.value} value={item.value}>
                            {t(item.label)}
                          </option>)}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {t(errors.genderPreference)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <MultiSelectTagField label={<>{t("Community Preference")}{" "}
                          <span className="bb-required-mark">*</span>
                        </>} name="communityPreference" options={communityPreferenceOptions} values={values} errors={errors} setFieldValue={setFieldValue} setFieldTouched={setFieldTouched} helperText={t("You can select one or multiple preferred communities.")} disabled={isCommunityPreferencesLoading} loading={isCommunityPreferencesLoading} searchable searchPlaceholder={t("Search community preference")} showError={getFieldErrorState("communityPreference")} onInteract={fieldName => markStepFieldInteracted(2, fieldName)} />

                  </Col>
                </Row>
              </div>}

            {currentStep === 3 && <div className="bb-form-section">
                <h3 className="bb-form-section__title">{t("Room Features & Contact Options")}

              </h3>

                <Row className="g-4">
                  <Col md={6}>
                    <CounterField label={<>{t("Total Bedrooms ")}<span className="bb-required-mark">*</span>
                        </>} name="bedrooms" value={values.bedrooms} setFieldValue={setFieldValue} setFieldTouched={setFieldTouched} error={errors.bedrooms} showError={getFieldErrorState("bedrooms")} min={1} onInteract={fieldName => markStepFieldInteracted(3, fieldName)} />

                  </Col>

                  <Col md={6}>
                    <CounterField label={<>{t("Total Bathrooms ")}<span className="bb-required-mark">*</span>
                        </>} name="bathrooms" value={values.bathrooms} setFieldValue={setFieldValue} setFieldTouched={setFieldTouched} error={errors.bathrooms} showError={getFieldErrorState("bathrooms")} min={1} onInteract={fieldName => markStepFieldInteracted(3, fieldName)} />

                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>{t("Kitchen Access ")}
                      <span className="bb-required-mark">*</span>
                      </Form.Label>
                      <Form.Select name="kitchenAccess" value={values.kitchenAccess} onChange={e => {
                  markStepFieldInteracted(3, "kitchenAccess");
                  handleChange(e);
                }} onBlur={e => {
                  markStepFieldInteracted(3, "kitchenAccess");
                  handleBlur(e);
                }} isInvalid={getFieldErrorState("kitchenAccess")}>

                        <option value="">{t("Select Kitchen Access")}</option>
                        {kitchenAccessOptions.map(item => <option key={item.value} value={item.value}>
                            {t(item.label)}
                          </option>)}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {t(errors.kitchenAccess)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>{t("AC Type ")}
                      <span className="bb-required-mark">*</span>
                      </Form.Label>
                      <Form.Select name="acType" value={values.acType} onChange={e => {
                  markStepFieldInteracted(3, "acType");
                  handleChange(e);
                }} onBlur={e => {
                  markStepFieldInteracted(3, "acType");
                  handleBlur(e);
                }} isInvalid={getFieldErrorState("acType")}>

                        <option value="">{t("Select AC Type")}</option>
                        {acTypeOptions.map(item => <option key={item.value} value={item.value}>
                            {t(item.label)}
                          </option>)}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {t(errors.acType)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <CounterField label={<>{t("Current Occupants ")}<span className="bb-required-mark">*</span>
                        </>} name="currentOccupants" value={values.currentOccupants} setFieldValue={setFieldValue} setFieldTouched={setFieldTouched} error={errors.currentOccupants} showError={getFieldErrorState("currentOccupants")} min={1} onInteract={fieldName => markStepFieldInteracted(3, fieldName)} />

                  </Col>

                  <Col md={6}>
                    <CounterField label={<>{t("Minimum Stay")}</>} name="minimumStay" value={values.minimumStay} setFieldValue={setFieldValue} setFieldTouched={setFieldTouched} error={errors.minimumStay} showError={getFieldErrorState("minimumStay")} min={1} helperText={t("Enter minimum stay duration in months.")} onInteract={fieldName => markStepFieldInteracted(3, fieldName)} />

                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>{t("Rent Payment Plan ")}
                      <span className="bb-required-mark">*</span>
                      </Form.Label>
                      <Form.Select name="rentPaymentPlan" value={values.rentPaymentPlan} onChange={e => {
                  markStepFieldInteracted(3, "rentPaymentPlan");
                  handleChange(e);
                }} onBlur={e => {
                  markStepFieldInteracted(3, "rentPaymentPlan");
                  handleBlur(e);
                }} isInvalid={getFieldErrorState("rentPaymentPlan")}>

                        <option value="">{t("Select Rent Payment Plan")}</option>
                        {rentPaymentPlanOptions.map(item => <option key={item.value} value={item.value}>
                            {t(item.label)}
                          </option>)}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {t(errors.rentPaymentPlan)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <div className="bb-toggle-card">
                      <div>
                        <h5 className="bb-toggle-card__title">{t("Furnished")}</h5>
                        <p className="bb-toggle-card__text">{t("Is the room fully furnished?")}

                      </p>
                      </div>

                      <Form.Check type="switch" id="isFurnished" name="isFurnished" checked={values.isFurnished} onChange={e => {
                  markStepFieldInteracted(3, "isFurnished");
                  setFieldValue("isFurnished", e.target.checked);
                }} className="bb-toggle-card__switch" />

                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="bb-toggle-card">
                      <div>
                        <h5 className="bb-toggle-card__title">{t("Electricity Included")}

                      </h5>
                        <p className="bb-toggle-card__text">{t("Turn this on if electricity charges are included in the rent.")}

                      </p>
                      </div>

                      <Form.Check type="switch" id="electricityIncluded" name="electricityIncluded" checked={values.electricityIncluded} onChange={e => {
                  markStepFieldInteracted(3, "electricityIncluded");
                  setFieldValue("electricityIncluded", e.target.checked);
                }} className="bb-toggle-card__switch" />

                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="bb-toggle-card">
                      <div>
                        <h5 className="bb-toggle-card__title">{t("Water Bill Included")}

                      </h5>
                        <p className="bb-toggle-card__text">{t("Turn this on if the water bill is included in the rent.")}

                      </p>
                      </div>

                      <Form.Check type="switch" id="waterBillIncluded" name="waterBillIncluded" checked={values.waterBillIncluded} onChange={e => {
                  markStepFieldInteracted(3, "waterBillIncluded");
                  setFieldValue("waterBillIncluded", e.target.checked);
                }} className="bb-toggle-card__switch" />

                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="bb-toggle-card">
                      <div>
                        <h5 className="bb-toggle-card__title">{t("Parking Available")}</h5>
                        <p className="bb-toggle-card__text">{t("Turn this on if parking is available with the property.")}

                      </p>
                      </div>

                      <Form.Check type="switch" id="parkingAvailable" name="parkingAvailable" checked={values.parkingAvailable} onChange={e => {
                  markStepFieldInteracted(3, "parkingAvailable");
                  setFieldValue("parkingAvailable", e.target.checked);
                }} className="bb-toggle-card__switch" />

                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="bb-toggle-card">
                      <div>
                        <h5 className="bb-toggle-card__title">{t("Allow WhatsApp Contact")}

                      </h5>
                        <p className="bb-toggle-card__text">{t("Let users contact you via WhatsApp for this room listing.")}

                      </p>
                      </div>

                      <Form.Check type="switch" id="allowWhatsAppContact" name="allowWhatsAppContact" checked={values.allowWhatsAppContact} onChange={e => {
                  handleToggleContactMethod("allowWhatsAppContact", e.target.checked);
                }} className="bb-toggle-card__switch" />

                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="bb-toggle-card">
                      <div>
                        <h5 className="bb-toggle-card__title">{t("Allow Direct Calls")}

                      </h5>
                        <p className="bb-toggle-card__text">{t("Let users directly call you for this room listing.")}

                      </p>
                      </div>

                      <Form.Check type="switch" id="allowDirectCalls" name="allowDirectCalls" checked={values.allowDirectCalls} onChange={e => {
                  handleToggleContactMethod("allowDirectCalls", e.target.checked);
                }} className="bb-toggle-card__switch" />

                    </div>
                  </Col>

                  <Col xs={12}>
                    {getFieldErrorState("contactPreference") || errors.contactPreference ? <div className="invalid-feedback d-block">
                        {t(errors.contactPreference)}
                      </div> : null}
                  </Col>
                </Row>
              </div>}

            {currentStep === 4 && <div className="bb-form-section">
                <h3 className="bb-form-section__title">{t("Room Images & Description")}

              </h3>

                <Row className="g-4">
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>{t("Upload from Device")}
                      {t(" ")}
                        <span className="bb-required-mark">*</span>
                      </Form.Label>

                      <div className={`bb-create-listing__upload-box ${getFieldErrorState("roomImages") ? "is-invalid" : ""}`}>

                        <HiOutlinePhotograph className="bb-create-listing__upload-icon" />

                        <div className="bb-create-listing__upload-copy">
                          <h6 className="bb-create-listing__upload-title">{t("Upload room images")}

                        </h6>
                          <p className="bb-create-listing__upload-text">{t("You can select multiple image files. Supported formats: JPG, JPEG, PNG, WEBP. Maximum 5 MB per image. Maximum 15 images allowed.")}



                        </p>
                        </div>

                        <Form.Control ref={fileInputRef} type="file" accept=".jpg,.jpeg,.png,.webp" multiple onChange={handleRoomImagesChange} isInvalid={false} />

                      </div>

                      {roomImagesUploadError ? <div className="invalid-feedback d-block">
                          {t(roomImagesUploadError)}
                        </div> : null}

                      {!roomImagesUploadError && !isCompressingImages && getFieldErrorState("roomImages") ? <div className="invalid-feedback d-block">
                          {t(errors.roomImages)}
                        </div> : null}
                    </Form.Group>
                  </Col>

                  <Col xs={12}>
                    {isCompressingImages ? <div className="bb-create-listing__preview-loading">
                        <Spinner animation="border" size="sm" />
                        <span>{t("Processing Images for Preview...")}</span>
                      </div> : Array.isArray(values.roomImages) && values.roomImages.length ? <div className="bb-create-listing__preview-grid">
                        {values.roomImages.map((file, index) => <RoomImagePreviewCard key={`room-image-preview-${index}-${isFileLikeObject(file) ? file?.name || "file" : getExistingImageId(file) || "existing"}`} file={file} index={index} onRemove={handleRemoveImage} />)}
                      </div> : null}
                  </Col>

                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label>{t("Description ")}
                      <span className="bb-required-mark">*</span>
                      </Form.Label>
                      <Form.Control as="textarea" rows={5} name="description" value={values.description} onChange={e => {
                  markStepFieldInteracted(4, "description");
                  handleChange(e);
                }} onBlur={e => {
                  markStepFieldInteracted(4, "description");
                  handleBlur(e);
                }} isInvalid={getFieldErrorState("description")} placeholder={t("Describe the room, the house environment, nearby access, and the type of roommate preferred.")} />

                      <Form.Control.Feedback type="invalid">
                        {t(errors.description)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              </div>}

            <div className="bb-create-listing__actions">
              {currentStep === 1 ? <>
                  <Button variant="outline-secondary" type="button" onClick={onCancel}>{t("Cancel")}


                </Button>

                  <Button type="button" className="bb-form-post-room__btn" onClick={handleStepNext} disabled={isCitiesLoading || isCommunityPreferencesLoading || isPropertyTypesLoading}>

                    {isCitiesLoading || isCommunityPreferencesLoading || isPropertyTypesLoading ? <>
                        <Spinner size="sm" animation="border" className="me-2" />{t("Loading...")}

                  </> : t("Next")}
                  </Button>
                </> : currentStep < 4 ? <>
                  <Button variant="outline-secondary" type="button" onClick={handleStepBack}>{t("Back")}


                </Button>

                  <Button type="button" className="bb-form-post-room__btn" onClick={handleStepNext}>{t("Next")}


                </Button>
                </> : <>
                  <Button variant="outline-secondary" type="button" onClick={handleStepBack}>{t("Back")}


                </Button>

                  <Button type="button" className="bb-form-post-room__btn" onClick={handleFinalSubmit} disabled={isSubmitting}>

                    {isSubmitting ? <>
                        <Spinner size="sm" animation="border" className="me-2" />{t("Posting...")}

                  </> : submitLabel}
                  </Button>
                </>}
            </div>
          </Form>;
    }}
    </Formik>;
}
